import React, { useCallback, useState, FC } from 'react'
import { EditorState, ContentBlock } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import 'react-image-crop/dist/ReactCrop.css'
import heic2any from 'heic2any'
import { useDispatch } from 'react-redux'
import styles from 'styles/components/wysiwyg.module.scss'
import { CautionModal } from 'components/Modal'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { uploadImageInWysiwygEditor } from 'lib/storage'
import { WysiwygMedia } from 'components/Form'

type Props = {
  currentLength: number
  editorState: EditorState | undefined
  folderPath: string
  maxLength: number
  onEditorStateChange: (editorState: EditorState) => void
  replaceImage: (src: string, dist: string) => void
}

const WysiwygEditor: FC<Props> = (props) => {
  const { currentLength, editorState, folderPath, maxLength, onEditorStateChange, replaceImage } = props
  const dispatch = useDispatch()
  const canvas = document.createElement('canvas')
  const [open, setOpen] = useState<boolean>(false)
  const [modalType, setModalType] = useState<'size' | 'type' | 'crop' | ''>('')
  const [fileName, setFileName] = useState<string>('')

  const blockRendererFn = (block: ContentBlock) => {
    const blockType = block.getType()
    if (blockType === 'atomic') {
      return {
        component: WysiwygMedia,
        editable: false,
        props: {
          folderPath,
          canvas,
          replaceImage,
        },
      }
    }
    return
  }

  const onSelectFile = async (file: File): Promise<Blob | null> => {
    // 画像形式のチェック
    if (
      file.type !== 'image/jpeg' &&
      file.type !== 'image/jpg' &&
      file.type !== 'image/png' &&
      file.type !== 'image/heic'
    ) {
      setFileName(file.name)
      setModalType('type')
      setOpen(true)
      return null
    }
    // ファイルサイズのチェック(5MB以上はアラート表示)
    if (file.size > 5242880) {
      setFileName(file.name)
      setModalType('size')
      setOpen(true)
      return null
    }
    // HEIC形式はそのままでは使えないのでJPEGに変換する
    if (file.type === 'image/heic') {
      dispatch(showLoadingAction())
      const blob = file as Blob
      const convertedBlob = await heic2any({
        blob,
        toType: 'image/jpeg',
        quality: 1,
      })
      dispatch(hideLoadingAction())
      return convertedBlob as Blob
    } else {
      return file
    }
  }

  const closeModal = useCallback(() => {
    setFileName('')
    setModalType('')
    setOpen(false)
  }, [setOpen, setModalType, setFileName])

  const uploadCallback = async (file: File) => {
    const blob = await onSelectFile(file)
    if (!blob) return
    const link = await uploadImageInWysiwygEditor(blob, blob.type, folderPath)
    return { data: { link } }
  }

  return (
    <div className={styles.container}>
      <Editor
        editorState={editorState}
        customBlockRenderFunc={blockRendererFn}
        onEditorStateChange={onEditorStateChange}
        toolbarClassName={styles.toolbar}
        toolbarStyle={{
          border: 'solid 1px #BABABA',
          borderRadius: '4px',
        }}
        wrapperClassName={styles.wrapper}
        editorClassName={styles.editor}
        editorStyle={{
          backgroundColor: '#ffffff',
          border: 'solid 1px #BABABA',
          borderRadius: '4px',
          padding: '0 16px',
        }}
        localization={{ locale: 'ja' }}
        toolbar={{
          options: ['inline', 'blockType', 'image', 'history'],
          inline: {
            options: ['bold'],
          },
          blockType: {
            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'],
          },
          list: {
            options: ['unordered', 'ordered'],
          },
          image: {
            alt: { present: false, mandatory: false },
            inputAccept: 'image/jpeg,image/jpg,image/png,image/heic',
            previewImage: true,
            uploadCallback: uploadCallback,
            urlEnabled: false,
          },
        }}
      />
      <div className={styles.lengthCount}>
        {currentLength}/{maxLength}
      </div>
      {open && modalType === 'type' && (
        <CautionModal
          cancelLabel={'戻る'}
          caution={'ファイル形式に問題があります'}
          closeModal={closeModal}
          description={`${fileName}はアップロードできません。\njpeg,jpg,pngの5MB以内の画像を選択してください。`}
          open={open}
          onClickCancel={closeModal}
        />
      )}
      {open && modalType === 'size' && (
        <CautionModal
          cancelLabel={'戻る'}
          caution={'画像サイズに問題があります'}
          closeModal={closeModal}
          description={`${fileName}は大き過ぎてアップロードできません。\nファイルのサイズは5.00 MB以下にしてください。`}
          open={open}
          onClickCancel={closeModal}
        />
      )}
    </div>
  )
}

export default WysiwygEditor
